import useResize from '@anm/hooks/useResize';
import useSwitchState from '@anm/hooks/useSwitchState';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';

import { analyticsSelectors } from '../../../../../../store/modules/analytics';
import { hostedVideoSelectors } from '../../../../../../store/modules/hostedVideo';
import { projectSelectors } from '../../../../../../store/modules/project';

import { getChartData } from './helpers';
import { MediaSize } from './types';

const DEFAULT_RATIO = 1;

export const useCalcNotRenderedPreviewHeight = () => {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const project = useSelector(projectSelectors.selectCurrentProject);
  const [previewHeight, setPreviewHeight] = useState(0);

  useEffect(() => {
    if (!placeholderRef.current) return;

    const ratio = (project && project.width / project.height) || DEFAULT_RATIO;
    const height = placeholderRef.current.clientWidth / ratio;

    setPreviewHeight(height);
  }, [placeholderRef.current]);

  return [placeholderRef, previewHeight] as const;
};

export const useVideo = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [isImageLoaded, setIsImageLoaded] = useSwitchState();
  const [mediaSizes, setMediaSizes] = useState<MediaSize>({
    width: 0,
    height: 0
  });
  const preview = useSelector(
    projectSelectors.selectCurrentProjectDefaultPreview
  )!;

  const videoUrl = useSelector(hostedVideoSelectors.selectVideoUrl);

  useLayoutEffect(() => {
    if (!isImageLoaded || !imageRef.current) return;

    const { clientWidth, clientHeight } = imageRef.current;

    setMediaSizes({
      width: clientWidth,
      height: clientHeight
    });
  }, [isImageLoaded, imageRef.current]);

  useResize(() => {
    if (!imageRef.current) return;

    setMediaSizes({
      width: imageRef.current.clientWidth,
      height: imageRef.current.clientHeight
    });
  });

  return {
    preview,
    videoRef,
    imageRef,
    videoUrl,
    setIsImageLoaded,
    chartSizes: mediaSizes
  };
};

export const useEngagementVideoData = () => {
  const videoProps = useVideo();

  const averageViews = useSelector(
    analyticsSelectors.selectAggregateEngagement
  );

  const renderTooltipContent = useCallback(
    () => (
      <span>
        On average, people watch <br />
        <b>{averageViews.toFixed(2)}%</b> of the video
      </span>
    ),
    [averageViews]
  );

  return [videoProps, averageViews, renderTooltipContent] as const;
};

export const useChartData = () => {
  const viewsData = useSelector(
    analyticsSelectors.selectAggregateEngagementViews
  );
  const isPending = useSelector(analyticsSelectors.selectPeriodDataIsPending);
  const isProjectEverRender = useSelector(
    projectSelectors.selectCurrentProjectEverRendered
  );
  const projectDuration = useSelector(
    projectSelectors.selectCurrentProjectDuration
  )!;
  const averageViews = useSelector(
    analyticsSelectors.selectAggregateEngagement
  );
  const chartData = getChartData(viewsData);

  return [
    chartData,
    isPending,
    averageViews,
    projectDuration,
    isProjectEverRender
  ] as const;
};
